import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import CryptoHoldings from './components/CryptoHoldings';
import SpotTradingForms from './components/SpotTradingForms';
import CFDTradingForms from './components/CFDTradingForms';
import FuturesTradingForms from './components/FuturesTradingForms';
import FuturesMyOrders from './components/FuturesMyOrders';
import TradingChart from './components/TradingChart';
import Markets from './components/Markets';
import SpotMyOrders from './components/SpotMyOrders';
import ArbitrageRobotPage from './components/ArbitrageRobotPage';
import EventBettingPage from './components/EventBettingPage';
import CryptoWithdrawalModal from './components/CryptoWithdrawalModal';
import PropFirmChallengePage from './components/PropFirmChallengePage';
import WalletPage from './components/WalletPage';
import ProfilePage from './components/ProfilePage';
import OrderBook from './components/OrderBook';
import StakingPage from './pages/StakingPage';
import HomePage from './pages/HomePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import LoadingScreen from './components/LoadingScreen';
import AuthModal from './components/AuthModal';
import { supabase } from './lib/supabaseClient';
import { useAuth } from './hooks/useAuth';
import { useDatabase } from './hooks/useDatabase';
import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from './constants/tradingPairs';
import { useUserAssets } from './hooks/useUserAssets';
import { useFuturesTrading } from './hooks/useFuturesTrading';
import { usePropFirmTrading } from './hooks/usePropFirmTrading';
import { useWalletBreakdown } from './hooks/useWalletBreakdown';
import SwapCryptoPage from './components/SwapCryptoPage';

export type TradingMode = 'home' | 'swap' | 'futures' | 'cfd' | 'prop_firm' | 'robot' | 'events' | 'wallet' | 'profile' | 'staking';

export interface FuturesPosition {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  amount: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  marginType: 'isolated' | 'cross';
  liquidationPrice: number;
  unrealizedPnl: number;
  margin: number;
  roi: number;
  created_at: string;
  user_email?: string;
}

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'trade' | 'robot_profit' | 'binary_trade' | 'stake' | 'staking_profit' | 'staking_return' | 'challenge_fee' | 'challenge_reward';
  amount: number;
  description: string;
  status: 'completed' | 'pending' | 'failed';
  timestamp: string;
}

// User status tiers based on portfolio value in USD
export type UserStatus = 'No-Coiner' | 'Shrimp' | 'Crab' | 'Octopus' | 'Dolphin' | 'Shark' | 'Whale' | 'Humpback';

function App() {
  const { user, loading: authLoading, signOut } = useAuth();
  const { 
    balances, 
    updateBalances, 
    fetchBalances,
    robotState, 
    updateRobotState, 
    marketData, 
    fetchMarketData, 
    loading: dbLoading, 
    transactions, 
    addTransaction,
    fetchTransactions,
    userStakes,
    addUserStake,
    eventOutcomes,
    eventBets,
    events,
    addEventBet,
    newsItems,
    portfolioSnapshots,
    createPortfolioSnapshot,
    fetchPortfolioSnapshots,
    kycStatus,
    updateKycStatus,
    referralCode,
    referralCount,
    referredUsers,
    fetchUserStakes,
    calculateCurrentEarnings,
    cancelUserStake,
    coingeckoMarketCapData
  } = useDatabase();
  const { assets, fetchAssets, updateAssetBalance } = useUserAssets();
  const { 
    activePositions, 
    openOrders, 
    fetchActivePositions, 
    fetchOpenOrders, 
    openPosition, 
    closePosition, 
    cancelOrder, 
    cancelAllOpenOrders,
    calculateLiquidationPrice
  } = useFuturesTrading();
  
  const {
    propPositions,
    propOrders,
    propPositionHistory,
    propChallengeAccount,
    fetchPropPositions,
    fetchPropOrders,
    fetchPropPositionHistory,
    fetchPropChallengeAccount,
    initializeChallenge,
    cancelChallenge,
    placePropOrder,
    closePropPosition,
    cancelPropOrder,
    cancelAllPropOrders,
    closeAllPropPositions,
    loading: propFirmLoading
  } = usePropFirmTrading();
  
  // Wallet breakdown for margin tracking
  const {
    totalBalance,
    usedMargin,
    unrealizedPnl,
    availableBalance,
    reserved,
    refreshBreakdown
  } = useWalletBreakdown();

  // Calculate actual available balance for trading
  const actualAvailableBalance = useMemo(() => {
    return Math.max(0, availableBalance);
  }, [availableBalance]);
  
  const [tradingMode, setTradingMode] = useState<TradingMode>('home');
  const [selectedPair, setSelectedPair] = useState('BTCUSDT');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [maxCfdLeverage, setMaxCfdLeverage] = useState(100);
  
  // Define tickers based on trading mode
  const tickers = tradingMode === 'cfd' ? CFD_INSTRUMENTS : TOP_CRYPTO_PAIRS;
  
  // Interval reference for position refresh
  const positionRefreshIntervalRef = useRef<number | null>(null);
  
  // Check if this is a password recovery link
  const isRecoveryLink = useMemo(() => {
    const hash = window.location.hash;
    if (!hash) return false;
    
    const hashParams = new URLSearchParams(hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    
    return type === 'recovery' && !!accessToken;
  }, []);

  // Handle auth callback from CRM impersonation
  useEffect(() => {
    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('access_token');
      const refreshToken = urlParams.get('refresh_token');
      
      if (accessToken) {
        try {
          console.log('Auth callback detected, handling CRM impersonation...');
          
          // Sign out current session first
          await supabase.auth.signOut();
          
          // Set the new session with the provided tokens
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || accessToken // Use access token as fallback if no refresh token
          });
          
          if (error) {
            console.error('Error setting session from auth callback:', error);
            throw error;
          }
          
          console.log('Successfully set session from auth callback:', data);
          
          // Clear the URL parameters to clean up the address bar
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          
        } catch (error) {
          console.error('Error handling auth callback:', error);
        }
      }
    };
    
    handleAuthCallback();
  }, []);

  // Update max CFD leverage based on selected pair and trading mode
  useEffect(() => {
    if (tradingMode === 'cfd') {
      const instrument = CFD_INSTRUMENTS.find(item => item.symbol === selectedPair);
      if (instrument) {
        switch (instrument.type) {
          case 'forex':
            setMaxCfdLeverage(100);
            break;
          case 'stock':
            setMaxCfdLeverage(20);
            break;
          case 'commodity':
            setMaxCfdLeverage(30);
            break;
          default:
            setMaxCfdLeverage(10); // Safe default
        }
      } else {
        setMaxCfdLeverage(10); // Safe default if instrument not found
      }
    }
  }, [selectedPair, tradingMode]);

  // Collect all active symbols for WebSocket subscription
  const activeSymbols = useMemo(() => {
    // Always ensure we have at least default symbols to prevent empty array
    const neededSymbols = new Set<string>(['BTCUSDT', 'ETHUSDT']); // Default symbols
    
    // Always include the selected pair (most important)
    if (selectedPair) {
      neededSymbols.add(selectedPair);
    }
    
    // Include symbols with active positions
    activePositions.forEach(p => {
      if (p.symbol) neededSymbols.add(p.symbol);
    });
    
    // Include symbols with prop positions
    propPositions.forEach(p => {
      if (p.symbol) neededSymbols.add(p.symbol);
    });
    
    // Convert to array with selectedPair first
    const symbolsArray = Array.from(neededSymbols);
    const finalSymbols = selectedPair ? 
      [selectedPair, ...symbolsArray.filter(s => s !== selectedPair)] : 
      symbolsArray;
    
    // Limit to 10 symbols max for better coverage
    const limitedSymbols = finalSymbols.slice(0, 10);
    
    console.log('[App] Active symbols for WebSocket subscription:', limitedSymbols);
    return limitedSymbols;
  }, [selectedPair, activePositions, propPositions]);
  
  // Always enable polling as fallback regardless of WebSocket status

  
  // Get current price from ticker or market data
  const currentSelectedPairPrice = useMemo(() => {
    const ticker = marketData.find(t => t.symbol === selectedPair);
    const marketPrice = marketData.find(data => data.symbol === selectedPair)?.price;
    if (marketPrice && marketPrice > 0) {
      return marketPrice;
    }
    
    return 0;
  }, [selectedPair, marketData]);

  // Define ticker variable for compatibility
  const ticker = marketData.find(data => data.symbol === selectedPair);

  // Calculate total portfolio value
 const totalPortfolioValue = useMemo(() => {
  let total = balances.usdt_balance; // Start with USDT balance

  // Get the current BTCUSDT price specifically
  const btcUsdtPrice = tickers['BTCUSDT']?.price ||
                       marketData.find(data => data.symbol === 'BTCUSDT')?.price ||
                       0;

  total += balances.btc_balance * btcUsdtPrice; // Add BTC value

  // Add value of other user assets
  const allAssets = assets || [];
  if (allAssets.length > 0) {
    allAssets.forEach(asset => {
    // Skip USDT and BTC as they are handled above
    if (asset.asset_symbol !== 'USDT' && asset.asset_symbol !== 'BTC') {
      // Construct the USDT pair symbol (e.g., 'ETHUSDT')
      const assetUsdtSymbol = `${asset.asset_symbol}USDT`;
      
      // Get the price from real-time tickers or fallback to marketData
      const assetPrice = tickers[assetUsdtSymbol]?.price ||
                         marketData.find(data => data.symbol === assetUsdtSymbol)?.price ||
                         0;
      
      total += asset.balance * assetPrice;
    }
    });
  }

  return total;
}, [balances, assets, tickers, marketData]);

  // Function to determine user status based on portfolio value in USD
  const getUserStatus = useCallback((portfolioValue: number): UserStatus => {
    if (portfolioValue <= 0) return 'No-Coiner';
    if (portfolioValue < 10000) return 'Shrimp';
    if (portfolioValue < 25000) return 'Crab';
    if (portfolioValue < 50000) return 'Octopus';
    if (portfolioValue < 100000) return 'Dolphin';
    if (portfolioValue < 500000) return 'Shark';
    if (portfolioValue < 2000000) return 'Whale';
    return 'Humpback';
  }, []);

  // Get user status
  const userStatus = getUserStatus(totalPortfolioValue);

  // Initialize app data - only when user is authenticated
  useEffect(() => {
    // Skip initialization if this is a recovery link
    if (isRecoveryLink) {
      setIsLoading(false);
      return;
    }
    
    const initializeData = async () => {
      // Only fetch user-specific data if user is authenticated
      if (user) {
        await fetchMarketData();
        await fetchTransactions();
        await fetchAssets();
        await fetchPortfolioSnapshots();
        await fetchUserStakes();
        await fetchActivePositions();
        await fetchOpenOrders();
      } else {
        // For unauthenticated users, only fetch public market data
        await fetchMarketData();
      }
      setIsLoading(false);
    };
    
    if (!authLoading && !dbLoading) {
      initializeData();
    }
  }, [
    user, 
    authLoading, 
    dbLoading, 
    fetchMarketData, 
    fetchTransactions, 
    fetchAssets, 
    fetchPortfolioSnapshots, 
    fetchUserStakes,
    fetchActivePositions,
    fetchOpenOrders,
    isRecoveryLink
  ]);

  // Set up periodic refresh of active positions - only for authenticated users
  useEffect(() => {
    // Only set up the interval if the user is logged in
    if (user) {
      // Clear any existing interval first
      if (positionRefreshIntervalRef.current) {
        clearInterval(positionRefreshIntervalRef.current);
      }
      
      // Set up a new interval to refresh positions every 10 seconds (reduced frequency since WebSocket handles real-time updates)
      positionRefreshIntervalRef.current = window.setInterval(async () => {
        console.log('Periodic refresh of positions...');
        
        try {
          // Refresh futures positions
          await fetchActivePositions();
          
          // Refresh prop firm positions if there's an active challenge
          if (propChallengeAccount) {
            await fetchPropPositions(propChallengeAccount.challengeId);
          }
        } catch (error) {
          console.error('Error refreshing positions:', error);
        }
      }, 10000); // 10 seconds interval (reduced since WebSocket provides real-time updates)
      
      // Clean up the interval when the component unmounts
      return () => {
        if (positionRefreshIntervalRef.current) {
          clearInterval(positionRefreshIntervalRef.current);
          positionRefreshIntervalRef.current = null;
        }
      };
    }
  }, [user, fetchActivePositions, fetchPropPositions, propChallengeAccount]);

  // Update position prices in real-time when WebSocket tickers change
  useEffect(() => {
    if (!user || !tickers || Object.keys(tickers).length === 0) return;
    
    const updatePositionPrices = async () => {
      try {
        // Get all symbols that have both active positions and WebSocket data
        const positionSymbols = activePositions.map(p => p.symbol);
        const propPositionSymbols = propPositions.map(p => p.symbol);
        const allPositionSymbols = [...new Set([...positionSymbols, ...propPositionSymbols])];
        
        // Update prices for symbols that have both positions and WebSocket data
        for (const symbol of allPositionSymbols) {
          const tickerData = tickers[symbol];
          if (tickerData && tickerData.price > 0) {
            // Update position current price in database
            const { error } = await supabase.rpc('update_position_current_price', {
              p_symbol: symbol,
              p_current_price: tickerData.price
            });
            
            if (error) {
              console.error(`Error updating position price for ${symbol}:`, error);
            }
          }
        }
      } catch (error) {
        console.error('Error updating position prices from WebSocket:', error);
      }
    };
    
    // Debounce the updates to avoid too frequent database calls
    const timeoutId = setTimeout(updatePositionPrices, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [tickers, activePositions, propPositions, user]);
  // Handle futures trade
  const handleFuturesTrade = useCallback(async (
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType: 'market' | 'limit' = 'market',
    contractSize?: number
  ) => {
    try {
      // Check available balance for margin requirement
      const notionalValue = amount * (contractSize || 1) * currentSelectedPairPrice;
      const requiredMargin = notionalValue / leverage;
      
      if (requiredMargin > availableBalance) {
        alert(`Insufficient available balance. Required: ${requiredMargin.toFixed(2)} USDT, Available: ${availableBalance.toFixed(2)} USDT`);
        return false;
      }
      
      // Fetch the most up-to-date balance before attempting to open a position
      await fetchBalances();
      
      // Use the openPosition function from useFuturesTrading hook
      const positionId = await openPosition({
        symbol: selectedPair,
        side,
        amount,
        leverage,
        marginType,
        orderType,
        price: currentSelectedPairPrice,
        stopLoss: stopLoss?.trigger_price,
        takeProfit: takeProfit?.trigger_price,
        contractSize
      });
      
      if (positionId) {
        // If it's a market order, add a transaction record
        if (orderType === 'market') {
          // Calculate margin
          const effectiveAmount = amount * (contractSize || 1);
          const notionalValue = effectiveAmount * currentSelectedPairPrice;
          const requiredMargin = notionalValue / leverage;
          
          // Add transaction record
          await addTransaction({
            type: 'trade',
            amount: -requiredMargin,
            description: `Opened ${side} position for ${selectedPair} with ${leverage}x leverage`,
            status: 'completed'
          });
        }
        
        // Refresh wallet breakdown to update available balance
        refreshBreakdown();
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in handleFuturesTrade:', error);
      return false;
    }
  }, [currentSelectedPairPrice, selectedPair, openPosition, addTransaction, availableBalance, refreshBreakdown]);

  // Handle closing a futures position
  const handleClosePosition = useCallback(async (positionId: string) => {
    try {
      // Call the closePosition function from useFuturesTrading hook
      const success = await closePosition(positionId);
      
      if (success) {
        // Refresh wallet breakdown to update available balance
        refreshBreakdown();
        
        // The closePosition function will handle updating the database and refreshing the positions
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in handleClosePosition:', error);
      return false;
    }
  }, [closePosition, refreshBreakdown]);

  // Handle spot order
  const handleSpotOrder = useCallback(async (order: {
    side: 'buy' | 'sell';
    order_type: 'market' | 'limit';
    amount: number;
    price?: number;
    stop_loss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number };
    take_profit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number };
  }) => {
    const { side, order_type, amount } = order;
    const price = order.price || currentSelectedPairPrice;
    
    // Calculate total cost/proceeds
    const total = amount * price;
    
    if (side === 'buy') {
      // Check if user has enough USDT
      if (total > balances.usdt_balance) {
        alert('Insufficient USDT balance');
        return;
      }
      
      // Update balances
      await updateBalances({ 
        usdt_balance: balances.usdt_balance - total,
        btc_balance: balances.btc_balance + amount
      });
      
      // Add transaction
      await addTransaction({
        type: 'trade',
        amount: -total,
        description: `Bought ${amount.toFixed(6)} BTC at ${price.toFixed(2)} USDT`,
        status: 'completed'
      });
    } else {
      // Check if user has enough BTC
      if (amount > balances.btc_balance) {
        alert('Insufficient BTC balance');
        return;
      }
      
      // Update balances
      await updateBalances({ 
        usdt_balance: balances.usdt_balance + total,
        btc_balance: balances.btc_balance - amount
      });
      
      // Add transaction
      await addTransaction({
        type: 'trade',
        amount: total,
        description: `Sold ${amount.toFixed(6)} BTC at ${price.toFixed(2)} USDT`,
        status: 'completed'
      });
    }
  }, [currentSelectedPairPrice, balances, updateBalances, addTransaction]);

  // Handle swap between cryptocurrencies
  const handleSwap = useCallback(async (fromSymbol: string, toSymbol: string, fromAmount: number, toAmountReceived?: number) => {
    try {
      // Wait for balances to be fully loaded
      if (dbLoading) {
        throw new Error('Please wait, loading account data...');
      }
      
      // Check available balance for swap
      const swapValueUSD = fromAmount * (fromSymbol === 'USDT' ? 1 : 
                                        fromSymbol === 'BTC' ? currentSelectedPairPrice :
                                        marketData.find(data => data.symbol === `${fromSymbol}USDT`)?.price || 0);
      
      if (swapValueUSD > availableBalance) {
        throw new Error(`Insufficient available balance. Required: ${swapValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
      }
      
      // Validate input parameters
      if (!fromSymbol || !toSymbol || isNaN(fromAmount) || fromAmount <= 0) {
        throw new Error('Invalid swap parameters');
      }

      // Additional security validations
      const MIN_SWAP_AMOUNT = 0.0001;
      const MAX_SWAP_AMOUNT_USD = 1000000;
      const MAX_SLIPPAGE = 0.05; // 5% maximum slippage
      const SWAP_FEE_RATE = 0.001; // 0.1% fee

      // Prevent swapping to the same currency
      if (fromSymbol === toSymbol) {
        throw new Error('Cannot swap to the same currency');
      }

      // Check minimum amount
      if (fromAmount < MIN_SWAP_AMOUNT) {
        throw new Error(`Minimum swap amount is ${MIN_SWAP_AMOUNT}`);
      }

      // Helper to get price for any symbol
      const getPriceForSymbol = (symbol: string): number => {
        if (symbol === 'USDT') return 1;
        // For BTC, use the current selected pair price if it's BTCUSDT, otherwise look up
        if (symbol === 'BTC') return tickers['BTCUSDT']?.price || marketData.find(d => d.symbol === 'BTCUSDT')?.price || 0;

        // For other cryptos, look up in tickers first, then marketData
        const usdtPair = `${symbol}USDT`;
        return tickers[usdtPair]?.price || marketData.find(d => d.symbol === usdtPair)?.price || 0;
      };

      // Get current prices
      const fromPrice = getPriceForSymbol(fromSymbol);
      const toPrice = getPriceForSymbol(toSymbol);

      // Validate prices are available and reasonable
      if (!fromPrice || !toPrice || fromPrice <= 0 || toPrice <= 0) {
        throw new Error('Unable to get current prices for swap');
      }

      // Check maximum USD value
      const fromUsdValue = fromAmount * fromPrice;
      if (fromUsdValue > MAX_SWAP_AMOUNT_USD) {
        throw new Error(`Maximum swap amount is $${MAX_SWAP_AMOUNT_USD.toLocaleString()}`);
      }
      
      // If toAmountReceived is not provided, calculate it
      let calculatedToAmount = toAmountReceived;
      if (!calculatedToAmount || isNaN(calculatedToAmount)) {
        const exchangeRate = fromPrice / toPrice;
        calculatedToAmount = fromAmount * exchangeRate * (1 - SWAP_FEE_RATE);
      } else {
        // If toAmountReceived is provided, validate it against expected calculation
        const expectedExchangeRate = fromPrice / toPrice;
        const expectedToAmount = fromAmount * expectedExchangeRate * (1 - SWAP_FEE_RATE);
        
        // Prevent division by zero or extremely small numbers
        if (expectedToAmount <= 0.00000001) {
          throw new Error('Calculated output amount is too small. Please increase the input amount.');
        }
        
        const slippage = Math.abs(calculatedToAmount - expectedToAmount) / expectedToAmount;
        
        // Additional sanity check: if slippage is unreasonably high, it's likely a calculation error
        if (slippage > 1.0) { // More than 100% slippage indicates a serious error
          throw new Error('Price calculation error detected. Please refresh the page and try again.');
        }
        
        if (slippage > MAX_SLIPPAGE) {
          throw new Error(`Price has changed too much (${(slippage * 100).toFixed(2)}% slippage). Please try again.`);
        }
      }
      
      // Validate calculated amount
      if (!calculatedToAmount || isNaN(calculatedToAmount) || calculatedToAmount <= 0) {
        throw new Error('Invalid amount to receive');
      }
      
      // Additional sanity check: ensure the swap ratio is reasonable

      const feeAmount = fromAmount * SWAP_FEE_RATE;

      // Helper to get current balance of any asset
      const getAssetBalance = (symbol: string): number => {
        if (symbol === 'USDT') return balances.usdt_balance;
        if (symbol === 'BTC') return balances.btc_balance;
        const asset = assets.find(a => a.asset_symbol === symbol);
        return asset ? asset.balance : 0;
      };

      const currentFromBalance = getAssetBalance(fromSymbol);
      
      // Final balance check with buffer for fees
      if (fromAmount > currentFromBalance) {
        throw new Error(`Insufficient ${fromSymbol} balance`);
      }

      // Ensure we're not trying to give out more than what's reasonable
      // Instead of checking numeric ratios, check USD values for reasonableness
      const toUsdValue = calculatedToAmount * toPrice;
      const valueRatio = toUsdValue / fromUsdValue;
      
      // The output USD value should be close to input USD value (accounting for fees)
      // Allow up to 10% difference to account for fees and minor price movements
      if (valueRatio > 1.1 || valueRatio < 0.8) {
        console.error('Swap validation failed:', {
          fromAmount,
          calculatedToAmount,
          fromPrice,
          toPrice,
          fromUsdValue,
          toUsdValue,
          valueRatio,
          expectedRatio: 'should be between 0.8 and 1.1'
        });
        throw new Error(`Swap calculation error: USD value mismatch (ratio: ${valueRatio.toFixed(3)}). Please refresh and try again.`);
      }

      // Log the swap for debugging purposes
      console.log('Executing swap:', {
        fromSymbol,
        toSymbol,
        fromAmount,
        calculatedToAmount,
        fromPrice,
        toPrice,
        exchangeRate: fromPrice / toPrice,
        feeAmount,
        fromBalance: currentFromBalance
      });

      // Deduct from fromSymbol balance
      if (fromSymbol === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance - fromAmount });
      } else if (fromSymbol === 'BTC') {
        await updateBalances({ btc_balance: balances.btc_balance - fromAmount });
      } else {
        await updateAssetBalance(fromSymbol, currentFromBalance - fromAmount);
      }

      // Add to toSymbol balance
      if (toSymbol === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance + calculatedToAmount });
      } else if (toSymbol === 'BTC') {
        await updateBalances({
          btc_balance: balances.btc_balance + calculatedToAmount
        });
      } else {
        const toAsset = assets.find(a => a.asset_symbol === toSymbol);
        if (toAsset) {
          await updateAssetBalance(toSymbol, toAsset.balance + calculatedToAmount);
        } else {
          // If the target asset doesn't exist for the user, create it
          // This will insert a new row in user_assets table
          // The updateAssetBalance function already handles this logic
          await updateAssetBalance(toSymbol, calculatedToAmount);
        }
      }
      
      // Add transaction record for swap
      await addTransaction({
        type: 'trade',
        amount: -fromAmount * getPriceForSymbol(fromSymbol), // Negative amount for the source currency
        description: `Swapped ${fromAmount} ${fromSymbol} to ${calculatedToAmount.toFixed(6)} ${toSymbol} (including 0.1% fee)`,
        status: 'completed'
      });
      
      // Refresh wallet breakdown
      refreshBreakdown();
      
      return true;
    } catch (error) {
      console.error('Swap error:', error);
      return false;
    }
  }, [currentSelectedPairPrice, balances, updateBalances, addTransaction, assets, updateAssetBalance, tickers, marketData, dbLoading, availableBalance, refreshBreakdown]);

  // Handle wallet operations (deposit/withdrawal)
  const handleWalletOperation = useCallback(async (type: 'deposit' | 'withdrawal', currency: 'USDT' | 'BTC', amount: number) => {
    if (type === 'deposit') {
      try {
        if (currency === 'USDT') {
          console.log(`Processing USDT deposit of ${amount} for user ${user?.id} (demo: ${user?.user_metadata?.is_demo ? 'yes' : 'no'})`);
          await updateBalances({ usdt_balance: balances.usdt_balance + amount });
        } else {
          await updateBalances({ btc_balance: balances.btc_balance + amount });
        }
        
        // Add transaction - this will trigger the demo account reset if user is a demo user
        const description = user?.user_metadata?.is_demo 
          ? `Initial deposit to convert demo account to live account: ${amount} ${currency}` 
          : `Deposited ${amount} ${currency}`;
        
        const { error: txError } = await addTransaction({
          type: 'deposit',
          amount,
          description,
          status: 'completed'
        });
        
        if (txError) {
          console.error('Error processing deposit transaction:', txError);
          throw new Error(`Failed to process deposit: ${txError.message}`);
        }        
      } catch (error) {
        console.error('Error processing deposit:', error);
        alert('Failed to process deposit. Please try again.');
      }
    } else {
      // Check available balance for withdrawal
      const withdrawalValueUSD = currency === 'USDT' ? amount : amount * currentSelectedPairPrice;
      
      if (withdrawalValueUSD > availableBalance) {
        alert(`Insufficient available balance. Required: ${withdrawalValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
        return;
      }
      
      // Check balance
      if (currency === 'USDT' && amount > balances.usdt_balance) {
        alert('Insufficient USDT balance');
        return;
      } else if (currency === 'BTC' && amount > balances.btc_balance) {
        alert('Insufficient BTC balance');
        return;
      }
      
      if (currency === 'USDT') {
        await updateBalances({ usdt_balance: balances.usdt_balance - amount });
      } else {
        await updateBalances({ btc_balance: balances.btc_balance - amount });
      }
      
      // Add transaction
      await addTransaction({
        type: 'withdrawal',
        amount: -amount,
        description: `Withdrawn ${amount} ${currency}`,
        status: 'completed'
      });
      
      // Refresh data after withdrawal
      await fetchBalances();
      await fetchAssets();
      await fetchTransactions();
    }
    
    // Refresh wallet breakdown
    refreshBreakdown();
  }, [balances, updateBalances, addTransaction, fetchBalances, fetchAssets, fetchTransactions, availableBalance, currentSelectedPairPrice, refreshBreakdown]);

  // Handle staking
  const handleStake = useCallback(async (asset: string, amount: number, duration: number) => {
    try {
      // Check available balance for staking
      const stakingValueUSD = asset === 'USDT' ? amount : amount * currentSelectedPairPrice;
      
      if (stakingValueUSD > availableBalance) {
        throw new Error(`Insufficient available balance. Required: ${stakingValueUSD.toFixed(2)} USD, Available: ${availableBalance.toFixed(2)} USD`);
      }
      
      // Check minimum stake
      if (amount < 0.001) {
        setStakingMessage({
          type: 'error',
          text: `Minimum stake amount is 0.001 ${asset}`
        });
        return;
      }
      
      // Check balance
      if (asset === 'USDT' && amount > balances.usdt_balance) {
        throw new Error(`Insufficient ${asset} balance`);
      } else if (asset === 'BTC' && amount > balances.btc_balance) {
        throw new Error(`Insufficient ${asset} balance`);
      }
      
      // Check if user already has an active stake for this asset
      if (hasActiveStakeForAsset(asset)) {
        throw new Error(`You already have an active stake for ${asset}. Please wait for it to complete before creating a new one.`);
      }
      
      // Calculate APY based on duration
      let apy = 5.0; // Base APY
      if (duration >= 90) apy = 7.5;
      if (duration >= 180) apy = 10.0;
      if (duration >= 365) apy = 12.5;
      
      setIsStaking(true);
      
      // Call the addUserStake function
      const success = await addUserStake(
        asset,
        amount,
        apy,
        duration
      );
      
      if (success) {
        setStakingMessage({
          type: 'success',
          text: `Successfully staked ${amount} ${asset} for ${duration} days`
        });
        
        // Close modal and reset form
        setShowConfirmationModal(false);
        setStakingAmount('');
        setSelectedAsset(null);
        
        // Call the onStake prop to update balances
        await onStake(asset, amount, duration);
        
        // Refresh user stakes
        await fetchUserStakes();
        
        // Refresh wallet breakdown
        refreshBreakdown();
      } else {
        throw new Error('Staking failed');
      }
    } catch (error: any) {
      setStakingMessage({
        type: 'error',
        text: error.message || 'Failed to stake. Please try again.'
      });
    } finally {
      setIsStaking(false);
    }
  }, [balances, updateBalances, addTransaction, addUserStake, fetchUserStakes, availableBalance, currentSelectedPairPrice, refreshBreakdown]);

  // Calculate time remaining for a stake
  // Show loading state while checking for existing challenge
  if (propFirmLoading && propChallengeAccount === null) {
    return <LoadingScreen message="Loading prop firm challenge data..." />;
  }

  // Set default pair when changing trading mode
  const handleTradingModeChange = (mode: TradingMode) => {
    setTradingMode(mode);
    
    // Set default pair based on trading mode
    if (mode === 'futures') {
      setSelectedPair('BTCUSDT');
    } else if (mode === 'cfd') {
      setSelectedPair('EURUSD');
    }
  };

  return (
    <Router>
      <div className="min-h-screen bg-slate-900 text-white">
        <Routes>
          {/* Handle password recovery at root path */}
          <Route path="/" element={
            isRecoveryLink ? (
              <ResetPasswordPage />
            ) : user ? (
              <>
                <Header 
                  tradingMode={tradingMode} 
                  setTradingMode={handleTradingModeChange}
                  selectedPair={selectedPair}
                  setSelectedPair={setSelectedPair}
                  currentPrice={currentSelectedPairPrice}
                  marketData={marketData.find(data => data.symbol === selectedPair)}
                  usdtBalance={balances.usdt_balance}
                  btcBalance={balances.btc_balance}
                  totalPortfolioValue={totalPortfolioValue}
                  user={user}
                  signOut={signOut}
                  marketDataList={marketData || []}
                  tickers={tickers}
                  userStatus={userStatus}
                />
                
                <main>
                  {tradingMode === 'home' && (
                    <HomePage 
                      currentBtcPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      marketData={marketData || []}
                      futuresPositions={activePositions}
                      transactions={transactions}
                      setTradingMode={setTradingMode}
                      newsItems={newsItems}
                    />
                  )}
                  
                  {tradingMode === 'swap' && (
                    <SwapCryptoPage
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentBtcPrice={currentSelectedPairPrice}
                      onSwap={handleSwap}
                      marketData={marketData}
                      availableBalance={actualAvailableBalance}
                      coingeckoMarketCapData={coingeckoMarketCapData}
                      fetchMarketData={fetchMarketData}
                      userAssets={assets}
                      fetchTransactions={fetchTransactions}
                    />
                  )}
                  
                  {tradingMode === 'futures' && (
                    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-900 overflow-y-auto">
                      {/* Left Column - Order Book (full width on mobile, fixed width on desktop) */}
                      <div className="hidden lg:block w-full lg:w-80 border-b lg:border-r lg:border-b-0 border-slate-700 h-auto lg:h-full">
                        <OrderBook 
                          currentPrice={currentSelectedPairPrice}
                          selectedPair={selectedPair}
                          tradingMode="futures"
                        />
                      </div>
                      
                      {/* Middle Column - Chart and Trading Forms */}
                      <div className="flex-1 flex flex-col h-full">
                        {/* Trading Chart */}
                        <div className="h-[40vh] lg:h-auto lg:flex-1">
                          <TradingChart key={selectedPair} selectedPair={selectedPair} />
                        </div>
                        
                        {/* Trading Forms and Positions */}
                        <div className="flex-1 flex flex-col">
                          {/* Trading Forms */}
                          <div className="p-2 lg:p-0">
                            <FuturesTradingForms 
                              currentBtcPrice={currentSelectedPairPrice}
                              usdtBalance={balances.usdt_balance}
                              availableBalance={actualAvailableBalance}
                              calculateLiquidationPrice={calculateLiquidationPrice}
                              onFuturesTrade={handleFuturesTrade}
                              selectedPair={selectedPair}
                            />
                          </div>
                          
                          {/* Positions - This div will now expand to fill available space */}
                          <div className="border-t border-slate-700 flex-1 overflow-auto">
                            <FuturesMyOrders 
                              currentBtcPrice={currentSelectedPairPrice}
                              futuresPositions={activePositions}
                              marketData={marketData || []}
                              onClosePosition={handleClosePosition}
                              openOrders={openOrders}
                              onCancelOrder={cancelOrder}
                              onCancelAllOrders={cancelAllOpenOrders}
                              updateBalances={updateBalances}
                              ticker={ticker}
                              tickers={tickers}
                              balances={balances}
                              selectedPair={selectedPair}
                              tradingMode={tradingMode}
                              currentSelectedPairPrice={currentSelectedPairPrice}
                            />
                          </div>
                        </div>
                      </div>
                    
                      
                      {/* Right Column - Markets (full width on mobile, fixed width on desktop) */}
                      <div className="hidden lg:block w-full lg:w-80 border-t lg:border-l lg:border-t-0 border-slate-700 h-auto lg:h-full">
                        <Markets 
                          marketData={marketData || []}
                          selectedPair={selectedPair}
                          setSelectedPair={setSelectedPair}
                          currentPrice={currentSelectedPairPrice}
                          tradingMode="futures"
                          tickers={tickers}
                          coingeckoMarketCapData={coingeckoMarketCapData}
                        />
                      </div>
                    </div>
                  )}
                  
                  {tradingMode === 'cfd' && (
                    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-900 overflow-y-auto">
                      {/* Left Column - Order Book (full width on mobile, fixed width on desktop) */}
                      <div className="hidden lg:block w-full lg:w-80 border-b lg:border-r lg:border-b-0 border-slate-700 h-auto lg:h-full">
                        <OrderBook 
                          currentPrice={currentSelectedPairPrice}
                          selectedPair={selectedPair}
                          tradingMode="cfd"
                        />
                      </div>
                      
                      {/* Middle Column - Chart and Trading Forms */}
                      <div className="flex-1 flex flex-col bg-slate-900 h-full">
                        {/* Trading Chart */}
                        <div className="h-[40vh] lg:h-auto lg:flex-1">
                          <TradingChart key={selectedPair} selectedPair={selectedPair} />
                        </div>
                        
                        {/* Trading Forms and Positions */}
                        <div className="flex-1 flex flex-col">
                          {/* Trading Forms */}
                          <div className="p-2 lg:p-0">
                            <CFDTradingForms 
                              currentPrice={currentSelectedPairPrice}
                              usdtBalance={balances.usdt_balance}
                              calculateLiquidationPrice={calculateLiquidationPrice}
                              selectedPair={selectedPair}
                              onCFDTrade={handleFuturesTrade}
                              maxAllowedLeverage={maxCfdLeverage}
                            />
                          </div>
                          
                          {/* Positions */}
                          <div className="border-t border-slate-700 flex-1 overflow-y-auto">
                            <FuturesMyOrders 
                              currentBtcPrice={currentSelectedPairPrice}
                              futuresPositions={activePositions}
                              marketData={marketData || []}
                              onClosePosition={handleClosePosition}
                              openOrders={openOrders}
                              onCancelOrder={cancelOrder}
                              onCancelAllOrders={cancelAllOpenOrders}
                              updateBalances={updateBalances}
                              ticker={ticker}
                              tickers={tickers}
                              balances={balances}
                              selectedPair={selectedPair}
                              tradingMode={tradingMode}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Right Column - Markets (full width on mobile, fixed width on desktop) */}
                      <div className="hidden lg:block w-full lg:w-80 border-t lg:border-l lg:border-t-0 border-slate-700 h-auto lg:h-full">
                        <Markets 
                          marketData={marketData || []}
                          selectedPair={selectedPair}
                          setSelectedPair={setSelectedPair}
                          currentPrice={currentSelectedPairPrice}
                          tradingMode="cfd"
                          tickers={tickers}
                          coingeckoMarketCapData={coingeckoMarketCapData}
                        />
                      </div>
                    </div>
                  )}
                  
                  {tradingMode === 'prop_firm' && (
                    <PropFirmChallengePage 
                      currentPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      selectedPair={selectedPair}
                      setSelectedPair={setSelectedPair}
                      marketData={marketData || []}
                      updateBalances={updateBalances}
                      addTransaction={addTransaction}
                      robotState={robotState}
                      updateRobotState={updateRobotState}
                      ticker={ticker}
                      tickers={tickers}
                      propPositions={propPositions}
                      propOrders={propOrders}
                      onClosePropPosition={(positionId) => closePropPosition(positionId, propChallengeAccount?.challengeId || '')}
                      onCancelPropOrder={(orderId) => cancelPropOrder(orderId, propChallengeAccount?.challengeId || '')}
                      onCancelAllPropOrders={() => propChallengeAccount ? cancelAllPropOrders(propChallengeAccount.challengeId) : Promise.resolve(false)}
                      currentBtcPrice={marketData.find(m => m.symbol === 'BTCUSDT')?.price || 0}
                    />
                  )}
                  
                  {tradingMode === 'robot' && (
                    <ArbitrageRobotPage 
                      currentBtcPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      availableBalance={availableBalance}
                      robotState={robotState}
                      onUpdateRobotState={updateRobotState}
                      onUpdateBalances={updateBalances}
                    />
                  )}
                  
                  {tradingMode === 'events' && (
                    <EventBettingPage 
                      events={events}
                      eventOutcomes={eventOutcomes}
                      eventBets={eventBets}
                      usdtBalance={balances.usdt_balance}
                      onEventBet={addEventBet}
                    />
                  )}
                  
                  {tradingMode === 'wallet' && (
                    <WalletPage 
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      transactions={transactions}
                      currentBtcPrice={currentSelectedPairPrice}
                      onWalletOperation={handleWalletOperation}
                      kycStatus={kycStatus}
                      marketData={marketData || []}
                      setTradingMode={setTradingMode}
                      userAssets={assets || []}
                      totalPortfolioValue={totalPortfolioValue}
                      walletBreakdown={{
                        totalBalance,
                        usedMargin,
                        unrealizedPnl,
                        availableBalance,
                        reserved
                      }}
                    />
                  )}
                  
                  {tradingMode === 'profile' && (
                    <ProfilePage 
                      user={user}
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentPrice={currentSelectedPairPrice}
                      onSignOut={signOut}
                      onUpdatePassword={async (password) => await supabase.auth.updateUser({ password })}
                      referralCode={referralCode}
                      referralCount={referralCount}
                      referredUsers={referredUsers}
                    />
                  )}
                  
                  {tradingMode === 'staking' && (
                    <StakingPage 
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentBtcPrice={currentSelectedPairPrice}
                      onStake={handleStake}
                    />
                  )}
                </main>
              </>
            ) : (
              <Navigate to="/sign-in" />
            )
          } />
          <Route path="/sign-in" element={user ? <Navigate to="/" /> : <SignInPage />} />
          <Route path="/sign-up" element={user ? <Navigate to="/" /> : <SignUpPage />} />
          <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
          <Route path="/reset-password" element={user ? <Navigate to="/" /> : <ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="*" element={
            user ? (
              <>
                <Header 
                  tradingMode={tradingMode} 
                  setTradingMode={handleTradingModeChange}
                  selectedPair={selectedPair}
                  setSelectedPair={setSelectedPair}
                  currentPrice={currentSelectedPairPrice}
                  marketData={marketData.find(data => data.symbol === selectedPair)}
                  usdtBalance={balances.usdt_balance}
                  btcBalance={balances.btc_balance}
                  totalPortfolioValue={totalPortfolioValue}
                  user={user}
                  signOut={signOut}
                  marketDataList={marketData || []}
                  tickers={tickers}
                  userStatus={userStatus}
                />
                
                <main>
                  {tradingMode === 'home' && (
                    <HomePage 
                      currentBtcPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      marketData={marketData || []}
                      futuresPositions={activePositions}
                      transactions={transactions}
                      setTradingMode={setTradingMode}
                      newsItems={newsItems}
                    />
                  )}
                  
                  {tradingMode === 'swap' && (
                    <SwapCryptoPage
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentBtcPrice={currentSelectedPairPrice}
                      onSwap={handleSwap}
                      marketData={marketData}
                      availableBalance={actualAvailableBalance}
                      coingeckoMarketCapData={coingeckoMarketCapData}
                      fetchMarketData={fetchMarketData}
                      userAssets={assets}
                      fetchTransactions={fetchTransactions}
                    />
                  )}
                  
                  {tradingMode === 'futures' && (
                    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-900 overflow-y-auto">
                      {/* Left Column - Order Book (full width on mobile, fixed width on desktop) */}
                      <div className="hidden lg:block w-full lg:w-80 border-b lg:border-r lg:border-b-0 border-slate-700 h-auto lg:h-full">
                        <OrderBook 
                          currentPrice={currentSelectedPairPrice}
                          selectedPair={selectedPair}
                          tradingMode="futures"
                        />
                      </div>
                      
                      {/* Middle Column - Chart and Trading Forms */}
                      <div className="flex-1 flex flex-col h-full">
                        {/* Trading Chart */}
                        <div className="h-[40vh] lg:h-auto lg:flex-1">
                          <TradingChart key={selectedPair} selectedPair={selectedPair} />
                        </div>
                        
                        {/* Trading Forms and Positions */}
                        <div className="flex-1 flex flex-col">
                          {/* Trading Forms */}
                          <div className="p-2 lg:p-0">
                            <FuturesTradingForms 
                              currentBtcPrice={currentSelectedPairPrice}
                              usdtBalance={balances.usdt_balance}
                              availableBalance={actualAvailableBalance}
                              calculateLiquidationPrice={calculateLiquidationPrice}
                              onFuturesTrade={handleFuturesTrade}
                              selectedPair={selectedPair}
                            />
                          </div>
                          
                          {/* Positions - This div will now expand to fill available space */}
                          <div className="border-t border-slate-700 flex-1 overflow-auto">
                            <FuturesMyOrders 
                              currentBtcPrice={currentSelectedPairPrice}
                              futuresPositions={activePositions}
                              marketData={marketData || []}
                              onClosePosition={handleClosePosition}
                              openOrders={openOrders}
                              onCancelOrder={cancelOrder}
                              onCancelAllOrders={cancelAllOpenOrders}
                              updateBalances={updateBalances}
                              ticker={ticker}
                              tickers={tickers}
                              balances={balances}
                              selectedPair={selectedPair}
                              tradingMode={tradingMode}
                              currentSelectedPairPrice={currentSelectedPairPrice}
                            />
                          </div>
                        </div>
                      </div>
                    
                      
                      {/* Right Column - Markets (full width on mobile, fixed width on desktop) */}
                      <div className="hidden lg:block w-full lg:w-80 border-t lg:border-l lg:border-t-0 border-slate-700 h-auto lg:h-full">
                        <Markets 
                          marketData={marketData || []}
                          selectedPair={selectedPair}
                          setSelectedPair={setSelectedPair}
                          currentPrice={currentSelectedPairPrice}
                          tradingMode="futures"
                          tickers={tickers}
                          coingeckoMarketCapData={coingeckoMarketCapData}
                        />
                      </div>
                    </div>
                  )}
                  
                  {tradingMode === 'cfd' && (
                    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-900 overflow-y-auto">
                      {/* Left Column - Order Book (full width on mobile, fixed width on desktop) */}
                      <div className="hidden lg:block w-full lg:w-80 border-b lg:border-r lg:border-b-0 border-slate-700 h-auto lg:h-full">
                        <OrderBook 
                          currentPrice={currentSelectedPairPrice}
                          selectedPair={selectedPair}
                          tradingMode="cfd"
                        />
                      </div>
                      
                      {/* Middle Column - Chart and Trading Forms */}
                      <div className="flex-1 flex flex-col bg-slate-900 h-full">
                        {/* Trading Chart */}
                        <div className="h-[40vh] lg:h-auto lg:flex-1">
                          <TradingChart key={selectedPair} selectedPair={selectedPair} />
                        </div>
                        
                        {/* Trading Forms and Positions */}
                        <div className="flex-1 flex flex-col">
                          {/* Trading Forms */}
                          <div className="p-2 lg:p-0">
                            <CFDTradingForms 
                              currentPrice={currentSelectedPairPrice}
                              usdtBalance={balances.usdt_balance}
                              calculateLiquidationPrice={calculateLiquidationPrice}
                              selectedPair={selectedPair}
                              onCFDTrade={handleFuturesTrade}
                              maxAllowedLeverage={maxCfdLeverage}
                            />
                          </div>
                          
                          {/* Positions */}
                          <div className="border-t border-slate-700 flex-1 overflow-y-auto">
                            <FuturesMyOrders 
                              currentBtcPrice={currentSelectedPairPrice}
                              futuresPositions={activePositions}
                              marketData={marketData || []}
                              onClosePosition={handleClosePosition}
                              openOrders={openOrders}
                              onCancelOrder={cancelOrder}
                              onCancelAllOrders={cancelAllOpenOrders}
                              updateBalances={updateBalances}
                              ticker={ticker}
                              tickers={tickers}
                              balances={balances}
                              selectedPair={selectedPair}
                              tradingMode={tradingMode}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Right Column - Markets (full width on mobile, fixed width on desktop) */}
                      <div className="hidden lg:block w-full lg:w-80 border-t lg:border-l lg:border-t-0 border-slate-700 h-auto lg:h-full">
                        <Markets 
                          marketData={marketData || []}
                          selectedPair={selectedPair}
                          setSelectedPair={setSelectedPair}
                          currentPrice={currentSelectedPairPrice}
                          tradingMode="cfd"
                          tickers={tickers}
                          coingeckoMarketCapData={coingeckoMarketCapData}
                        />
                      </div>
                    </div>
                  )}
                  
                  {tradingMode === 'prop_firm' && (
                    <PropFirmChallengePage 
                      currentPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      selectedPair={selectedPair}
                      setSelectedPair={setSelectedPair}
                      marketData={marketData || []}
                      updateBalances={updateBalances}
                      addTransaction={addTransaction}
                      robotState={robotState}
                      updateRobotState={updateRobotState}
                      ticker={ticker}
                      tickers={tickers}
                      propPositions={propPositions}
                      propOrders={propOrders}
                      onClosePropPosition={(positionId) => closePropPosition(positionId, propChallengeAccount?.challengeId || '')}
                      onCancelPropOrder={(orderId) => cancelPropOrder(orderId, propChallengeAccount?.challengeId || '')}
                      onCancelAllPropOrders={() => propChallengeAccount ? cancelAllPropOrders(propChallengeAccount.challengeId) : Promise.resolve(false)}
                      currentBtcPrice={marketData.find(m => m.symbol === 'BTCUSDT')?.price || 0}
                    />
                  )}
                  
                  {tradingMode === 'robot' && (
                    <ArbitrageRobotPage 
                      currentBtcPrice={currentSelectedPairPrice}
                      usdtBalance={balances.usdt_balance}
                      availableBalance={availableBalance}
                      robotState={robotState}
                      onUpdateRobotState={updateRobotState}
                      onUpdateBalances={updateBalances}
                    />
                  )}
                  
                  {tradingMode === 'events' && (
                    <EventBettingPage 
                      events={events}
                      eventOutcomes={eventOutcomes}
                      eventBets={eventBets}
                      usdtBalance={balances.usdt_balance}
                      onEventBet={addEventBet}
                    />
                  )}
                  
                  {tradingMode === 'wallet' && (
                    <WalletPage 
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      transactions={transactions}
                      currentBtcPrice={currentSelectedPairPrice}
                      onWalletOperation={handleWalletOperation}
                      kycStatus={kycStatus}
                      marketData={marketData || []}
                      setTradingMode={setTradingMode}
                      userAssets={assets || []}
                      totalPortfolioValue={totalPortfolioValue}
                      walletBreakdown={{
                        totalBalance,
                        usedMargin,
                        unrealizedPnl,
                        availableBalance,
                        reserved
                      }}
                    />
                  )}
                  
                  {tradingMode === 'profile' && (
                    <ProfilePage 
                      user={user}
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentPrice={currentSelectedPairPrice}
                      onSignOut={signOut}
                      onUpdatePassword={async (password) => await supabase.auth.updateUser({ password })}
                      referralCode={referralCode}
                      referralCount={referralCount}
                      referredUsers={referredUsers}
                    />
                  )}
                  
                  {tradingMode === 'staking' && (
                    <StakingPage 
                      usdtBalance={balances.usdt_balance}
                      btcBalance={balances.btc_balance}
                      currentBtcPrice={currentSelectedPairPrice}
                      onStake={handleStake}
                    />
                  )}
                </main>
              </>
            ) : (
              <Navigate to="/sign-in" />
            )
          } />
        </Routes>
        
        {!user && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />}
      </div>
    </Router>
  );
}

export default App;